import {
  UserRoleList,
  UserAuthorizationList,
  UserDataType,
  FallbackResult,
  FallbackFunction,
  failureTypeEnum,
} from "./router.types";
import { PAGE_URLS } from "utils/constants";

export class RouterFallbackResolver {
  // Authentication level fallbacks for required roles
  private requiredAuthFallbacks: Record<UserRoleList, FallbackResult> = {
    [UserRoleList.CREATOR]: {
      url: PAGE_URLS.SOCIALMEDIALOGIN,
      message: "Creator account required",
    },
    [UserRoleList.FIREBASE_AUTH]: {
      url: PAGE_URLS.FIREBASELOGIN,
      message: "Authentication required",
    },
    [UserRoleList.CREATOR_ADMIN]: {
      url: PAGE_URLS.HOME,
      message: "Admin access required",
    },
  };

  // Authentication level fallbacks for blocked roles
  private blockedAuthFallbacks: Record<UserRoleList, FallbackResult> = {
    [UserRoleList.CREATOR]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available for creator accounts",
    },
    [UserRoleList.FIREBASE_AUTH]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available for authenticated users",
    },
    [UserRoleList.CREATOR_ADMIN]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available for admin accounts",
    },
  };

  // Authorization level fallbacks for required authorizations
  private requiredAuthorizationFallbacks: Record<
    UserAuthorizationList,
    FallbackResult
  > = {
    [UserAuthorizationList.SOCIAL_LOGIN_MILESTONE_COMPLETE]: {
      url: PAGE_URLS.SOCIALMEDIALOGIN,
      message: "Social media connection required",
    },
    [UserAuthorizationList.CREATOR_SOURCING_ENABLED]: {
      url: PAGE_URLS.SOURCING_ACCESS,
      message: "Sourcing access required",
    },
    [UserAuthorizationList.CREATOR_BRAND_SOURCING_ENABLED]: {
      url: PAGE_URLS.BRAND_SOURCING_FTUX,
      message: "Brand sourcing access required",
    },
    [UserAuthorizationList.CREATOR_INSTAGRAM_LOGIN_COMPLETE]: {
      url: PAGE_URLS.HOME,
      message: "Instagram connection required",
    },
    [UserAuthorizationList.CREATOR_YOUTUBE_LOGIN_COMPLETE]: {
      url: PAGE_URLS.HOME,
      message: "YouTube connection required",
    },
    [UserAuthorizationList.CREATOR_TELEGRAM_LOGIN_COMPLETE]: {
      url: PAGE_URLS.HOME,
      message: "Telegram connection required",
    },
    [UserAuthorizationList.CREATOR_FACEBOOK_LOGIN_COMPLETE]: {
      url: PAGE_URLS.HOME,
      message: "Facebook connection required",
    },
    [UserAuthorizationList.CREATOR_BLOCKED]: {
      url: PAGE_URLS.USER_BLOCKED,
      message: "Your account has been blocked",
    },
    [UserAuthorizationList.CREATOR_KYC_COMPLETE]: {
      url: PAGE_URLS.KYC,
      message: "KYC verification required",
    },
    [UserAuthorizationList.ENGAGE_ACTIVE]: {
      url: PAGE_URLS.HOME,
      message: "Engage access required",
    },
    [UserAuthorizationList.ENGAGE_FACEBOOK_ACTIVE]: {
      url: PAGE_URLS.HOME,
      message: "FacebookEngage access required",
    },
    [UserAuthorizationList.CREATOR_SOCIAL_LOGIN_ACTIVE]: {
      url: PAGE_URLS.SOCIALMEDIALOGIN,
      message: "Social login required",
    },
    [UserAuthorizationList.CREATOR_WAITLISTED]: {
      url: PAGE_URLS.HOME,
      message: "You are on the waitlist",
    },
    [UserAuthorizationList.CREATOR_SOCIAL_ACCOUNT_TYPE]: {
      url: PAGE_URLS.HOME,
      message: "Social account type verification required",
    },
    [UserAuthorizationList.BRAND_COLLAB__COLLAB_LIVE]: {
      url: PAGE_URLS.HOME,
      message: "Brand collaboration access required",
    },
    [UserAuthorizationList.BRAND_COLLAB__COLLAB_CHAT_LIVE]: {
      url: PAGE_URLS.HOME,
      message: "Brand collaboration chat access required",
    },
    [UserAuthorizationList.COLLAGE_V1__INTERNAL_CREATORS]: {
      url: PAGE_URLS.HOME,
      message: "Internal creator access required",
    },
    [UserAuthorizationList.COLLAGE_V1__COLLAGE_LIVE]: {
      url: PAGE_URLS.HOME,
      message: "Collage access required",
    },
    [UserAuthorizationList.GROWTH_V2__PRODUCT_GROWTH]: {
      url: PAGE_URLS.HOME,
      message: "Growth access required",
    },
    [UserAuthorizationList.AUTO_CONTENT__AUTO_CONTENT_ENABLED]: {
      url: PAGE_URLS.HOME,
      message: "Auto content access required",
    },
    [UserAuthorizationList.NEW_SOCIAL_LOGIN_EDUCATION__SOCIAL_LOGIN_EDUCATION_REQUIRED]:
      {
        url: PAGE_URLS.HOME,
        message: "Social login education required",
      },
    [UserAuthorizationList.BRAND_COLLAB__COLLAB_COMING_SOON]: {
      url: PAGE_URLS.HOME,
      message: "Brand collaboration coming soon",
    },
    [UserAuthorizationList.ENGAGE_SERVICE__ENGAGE_VIA_FACEBOOK]: {
      url: PAGE_URLS.HOME,
      message: "Facebook engage access required",
    },
    [UserAuthorizationList.REFERRAL_PROGRAM__REFERRAL_DISABLED]: {
      url: PAGE_URLS.HOME,
      message: "Referral program should be disabled to access this feature",
    },
    [UserAuthorizationList.RTC__MAINTENANCE]: {
      url: PAGE_URLS.SOURCING_MAINTENANCE,
      message: "This feature is in maintenance",
    },
    [UserAuthorizationList.RTC__REFRESH]: {
      url: PAGE_URLS.SOURCING_MAINTENANCE,
      message: "This feature is in maintenance",
    },
    [UserAuthorizationList.RTC_SELECTIVE__LIVE]: {
      url: PAGE_URLS.HOME,
      message: "This feature is in maintenance",
    },
    [UserAuthorizationList.RTC_FINAL_APPROVAL__BLOCKED]: {
      url: PAGE_URLS.SOURCING_MAINTENANCE,
      message: "This feature is in maintenance",
    },
    [UserAuthorizationList.MEESHO_QR_CODE__QR_ENABLED]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available for Meesho QR enabled accounts",
    },
    [UserAuthorizationList.MEESHO_DISPUTE__DISPUTE_ENABLED]: {
      url: PAGE_URLS.HOME,
      message: "Meesho Dispute feature",
    },
    [UserAuthorizationList.FACEBOOK_SERVICE__CHANNEL_ENABLED]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available",
    },
    [UserAuthorizationList.CROSS_POSTING__POSTING_ENABLED]: {
      url: PAGE_URLS.SOCIALMEDIALOGIN,
      message: "This feature is not available",
    },
    [UserAuthorizationList.REWARDS__REWARDS_LIVE]: {
      url: PAGE_URLS.HOME,
      message: "Rewards not enabled",
    },
    [UserAuthorizationList.ENGAGE_CONNECTED]: {
      url: PAGE_URLS.HOME,
      message: "Engage not connected",
    },
    [UserAuthorizationList.FIRST_POST]: {
      url: PAGE_URLS.HOME,
      message: "First post not connected",
    },
    [UserAuthorizationList.FTUX_BRAND_COLLAB]: {
      url: PAGE_URLS.HOME,
      message: "Brand collaboration FTUX not completed",
    },
    [UserAuthorizationList.FTUX_GROWTH_V2]: {
      url: PAGE_URLS.HOME,
      message: "Growth FTUX not completed",
    },
    [UserAuthorizationList.FTUX_REWARDS]: {
      url: PAGE_URLS.HOME,
      message: "Rewards FTUX not completed",
    },
    [UserAuthorizationList.FTUX_SOURCING]: {
      url: PAGE_URLS.HOME,
      message: "Sourcing FTUX not completed",
    },
    [UserAuthorizationList.FTUX_ENGAGE]: {
      url: PAGE_URLS.HOME,
      message: "Engage FTUX not completed",
    },
    [UserAuthorizationList.FTUX_COLLAB_CHAT]: {
      url: PAGE_URLS.HOME,
      message: "Collab chat FTUX not completed",
    },
  };

  // Authorization level fallbacks for blocked authorizations
  private blockedAuthorizationFallbacks: Record<
    UserAuthorizationList,
    FallbackResult
  > = {
    [UserAuthorizationList.SOCIAL_LOGIN_MILESTONE_COMPLETE]: {
      url: PAGE_URLS.HOME,
      message:
        "This feature is not available for social media connected accounts",
    },
    [UserAuthorizationList.CREATOR_SOURCING_ENABLED]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available for sourcing enabled accounts",
    },
    [UserAuthorizationList.CREATOR_BRAND_SOURCING_ENABLED]: {
      url: PAGE_URLS.HOME,
      message: "This feature is available for brand sourcing enabled accounts",
    },
    [UserAuthorizationList.CREATOR_INSTAGRAM_LOGIN_COMPLETE]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available for Instagram connected accounts",
    },
    [UserAuthorizationList.CREATOR_YOUTUBE_LOGIN_COMPLETE]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available for YouTube connected accounts",
    },
    [UserAuthorizationList.CREATOR_TELEGRAM_LOGIN_COMPLETE]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available for Telegram connected accounts",
    },
    [UserAuthorizationList.CREATOR_FACEBOOK_LOGIN_COMPLETE]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available for Facebook connected accounts",
    },
    [UserAuthorizationList.CREATOR_BLOCKED]: {
      url: PAGE_URLS.USER_BLOCKED,
      message: "Your account has been blocked",
    },
    [UserAuthorizationList.CREATOR_KYC_COMPLETE]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available for KYC verified accounts",
    },
    [UserAuthorizationList.ENGAGE_ACTIVE]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available for Engage active accounts",
    },
    [UserAuthorizationList.ENGAGE_FACEBOOK_ACTIVE]: {
      url: PAGE_URLS.HOME,
      message:
        "This feature is not available for Facebook Engage active accounts",
    },
    [UserAuthorizationList.CREATOR_SOCIAL_LOGIN_ACTIVE]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available for social login active accounts",
    },
    [UserAuthorizationList.CREATOR_WAITLISTED]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available for waitlisted accounts",
    },
    [UserAuthorizationList.CREATOR_SOCIAL_ACCOUNT_TYPE]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available for your account type",
    },
    [UserAuthorizationList.BRAND_COLLAB__COLLAB_LIVE]: {
      url: PAGE_URLS.HOME,
      message:
        "This feature is not available for brand collaboration enabled accounts",
    },
    [UserAuthorizationList.BRAND_COLLAB__COLLAB_CHAT_LIVE]: {
      url: PAGE_URLS.HOME,
      message: "Brand collaboration chat access required",
    },
    [UserAuthorizationList.COLLAGE_V1__INTERNAL_CREATORS]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available for internal creators",
    },
    [UserAuthorizationList.COLLAGE_V1__COLLAGE_LIVE]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available for collage enabled accounts",
    },
    [UserAuthorizationList.GROWTH_V2__PRODUCT_GROWTH]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available for growth enabled accounts",
    },
    [UserAuthorizationList.AUTO_CONTENT__AUTO_CONTENT_ENABLED]: {
      url: PAGE_URLS.HOME,
      message:
        "This feature is not available for auto content enabled accounts",
    },
    [UserAuthorizationList.NEW_SOCIAL_LOGIN_EDUCATION__SOCIAL_LOGIN_EDUCATION_REQUIRED]:
      {
        url: PAGE_URLS.HOME,
        message:
          "This feature is not available for accounts requiring social login education",
      },
    [UserAuthorizationList.BRAND_COLLAB__COLLAB_COMING_SOON]: {
      url: PAGE_URLS.HOME,
      message:
        "This feature is not available for brand collaboration coming soon accounts",
    },
    [UserAuthorizationList.ENGAGE_SERVICE__ENGAGE_VIA_FACEBOOK]: {
      url: PAGE_URLS.HOME,
      message:
        "This feature is not available for Facebook engage enabled accounts",
    },
    [UserAuthorizationList.REFERRAL_PROGRAM__REFERRAL_DISABLED]: {
      url: PAGE_URLS.HOME,
      message:
        "This feature is not available for accounts with referral program disabled",
    },
    [UserAuthorizationList.MEESHO_QR_CODE__QR_ENABLED]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available for Meesho QR enabled accounts",
    },
    [UserAuthorizationList.MEESHO_DISPUTE__DISPUTE_ENABLED]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available for your account",
    },
    [UserAuthorizationList.RTC__MAINTENANCE]: {
      url: PAGE_URLS.SOURCING_MAINTENANCE,
      message: "This feature is in maintenance",
    },
    [UserAuthorizationList.RTC__REFRESH]: {
      url: PAGE_URLS.SOURCING_MAINTENANCE,
      message: "This feature is in maintenance",
    },
    [UserAuthorizationList.RTC_SELECTIVE__LIVE]: {
      url: PAGE_URLS.HOME,
      message: "This feature is in maintenance",
    },
    [UserAuthorizationList.RTC_FINAL_APPROVAL__BLOCKED]: {
      url: PAGE_URLS.SOURCING_MAINTENANCE,
      message: "This feature is in maintenance",
    },
    [UserAuthorizationList.FACEBOOK_SERVICE__CHANNEL_ENABLED]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available",
    },
    [UserAuthorizationList.CROSS_POSTING__POSTING_ENABLED]: {
      url: PAGE_URLS.HOME,
      message: "This feature is not available",
    },
    [UserAuthorizationList.REWARDS__REWARDS_LIVE]: {
      url: PAGE_URLS.HOME,
      message: "Rewards not enabled",
    },
    [UserAuthorizationList.ENGAGE_CONNECTED]: {
      url: PAGE_URLS.HOME,
      message: "Engage not connected",
    },
    [UserAuthorizationList.FIRST_POST]: {
      url: PAGE_URLS.HOME,
      message: "First post not connected",
    },
    [UserAuthorizationList.FTUX_BRAND_COLLAB]: {
      url: PAGE_URLS.HOME,
      message: "Brand collaboration FTUX not completed",
    },
    [UserAuthorizationList.FTUX_GROWTH_V2]: {
      url: PAGE_URLS.HOME,
      message: "Growth FTUX not completed",
    },
    [UserAuthorizationList.FTUX_REWARDS]: {
      url: PAGE_URLS.HOME,
      message: "Rewards FTUX not completed",
    },
    [UserAuthorizationList.FTUX_SOURCING]: {
      url: PAGE_URLS.HOME,
      message: "Sourcing FTUX not completed",
    },
    [UserAuthorizationList.FTUX_ENGAGE]: {
      url: PAGE_URLS.HOME,
      message: "Engage FTUX not completed",
    },
    [UserAuthorizationList.FTUX_COLLAB_CHAT]: {
      url: PAGE_URLS.HOME,
      message: "Collab chat FTUX not completed",
    },
  };

  resolveFallback(
    userData: UserDataType,
    failureType?: failureTypeEnum,
    failedValue?: string,
    customFallback?: FallbackFunction,
    isBlocked?: boolean
  ): FallbackResult {
    // Authentication failures take highest priority
    if (failureType === failureTypeEnum.AUTHENTICATION && failedValue) {
      const fallbackMap = isBlocked
        ? this.blockedAuthFallbacks
        : this.requiredAuthFallbacks;
      const authFallback = fallbackMap[failedValue as UserRoleList];
      if (authFallback) {
        return authFallback;
      }
    }

    // For authorization failures, try custom fallback first
    if (failureType === failureTypeEnum.AUTHORIZATION) {
      // Try custom fallback if provided
      if (customFallback) {
        return customFallback(userData);
      }

      // Then try authorization-specific fallbacks
      if (failedValue) {
        const fallbackMap = isBlocked
          ? this.blockedAuthorizationFallbacks
          : this.requiredAuthorizationFallbacks;

        const authorizationFallback =
          fallbackMap[failedValue as UserAuthorizationList];
        if (authorizationFallback) {
          return authorizationFallback;
        }
      }
    }

    // Default fallback if nothing else matches
    return this.getDefaultFallback();
  }

  getDefaultFallback(): FallbackResult {
    return { url: PAGE_URLS.HOME, message: "Access denied" };
  }
}

// Create a singleton instance
export const fallbackResolver = new RouterFallbackResolver();
